import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import type { PaymentError, StripeLikeError } from "../../CreatePermitWizard/components/PaymentForm/types";

type StripeCode =
	| "card_declined"
	| "generic_decline"
	| "insufficient_funds"
	| "lost_card"
	| "stolen_card"
	| "card_velocity_exceeded"
	| "expired_card"
	| "incorrect_cvc"
	| "incorrect_number"
	| "processing_error"
	| "invalid_postal_code"
	| "incomplete_number"
	| "invalid_expiry_month"
	| "invalid_expiry_year"
	| "invalid_expiry_year_past"
	| "invalid_cvc"
	| "invalid_number"
	| "incomplete_expiry"
	| "incomplete_cvc"
	| "incomplete_zip"
	| "incorrect_zip"
	| "payment_intent_authentication_failure";

type AuthorizeNetCode =
	| "2"
	| "3"
	| "6"
	| "7"
	| "8"
	| "11"
	| "17"
	| "27"
	| "37"
	| "44"
	| "45"
	| "65"
	| "78"
	| "315"
	| "316"
	| "317";

const DEFAULT_PAYMENT_ERROR_MESSAGE = msg`There was an error while processing your payment. Please try again in a few minutes or contact your administrator for support`;
const NOT_PAID_MESSAGE = msg`Your payment didn't go through. Check your card details and try again, or use a different card.`;

const STRIPE_PAYMENT_ERROR_MESSAGES: Record<StripeCode, MessageDescriptor> = {
	card_declined: msg`Your card was declined. Please try a different card or contact your bank.`,
	generic_decline: msg`Your card was declined. Please try a different card or contact your bank.`,
	insufficient_funds: msg`Your card has insufficient funds. Please try a different card.`,
	lost_card: msg`Your card has been reported lost. Please use a different card.`,
	stolen_card: msg`Your card has been reported stolen. Please use a different card.`,
	card_velocity_exceeded: msg`Your card was declined for making repeated attempts too frequently. Please wait a few minutes before trying again.`,
	expired_card: msg`Your card has expired. Please use a card with a valid expiration date.`,
	incorrect_cvc: msg`Your card's security code (CVV/CVC) is incorrect. Please check the 3-digit code on the back of your card.`,
	incorrect_number: msg`Your card number is invalid. Please check and re-enter your card number.`,
	processing_error: msg`An error occurred while processing your card. Please try again or use a different card.`,
	invalid_postal_code: msg`Your postal code is invalid. Please enter a valid postal code that matches your billing address.`,
	incomplete_number: msg`Your card number is incomplete. Please enter all digits of your card number.`,
	invalid_expiry_month: msg`The expiration month is invalid. Please enter a valid month (01-12).`,
	invalid_expiry_year: msg`The expiration year is invalid. Please enter a valid year.`,
	invalid_expiry_year_past: msg`The expiration year is in the past. Please enter a valid future expiration date.`,
	invalid_cvc: msg`Your card's security code (CVV/CVC) is invalid. Please check the 3-digit code on the back of your card.`,
	invalid_number: msg`Your card number is invalid. Please check and re-enter your card number.`,
	incomplete_expiry: msg`Your card's expiration date is incomplete. Please enter the full expiration date (MM/YY).`,
	incomplete_cvc: msg`Your card's security code is incomplete. Please enter the full CVV/CVC.`,
	incomplete_zip: msg`Your postal code is incomplete. Please enter your full postal code.`,
	incorrect_zip: msg`Your postal code does not match your billing address. Please verify and try again.`,
	payment_intent_authentication_failure: msg`We couldn't verify your card with your bank. Please try again or use a different card.`,
};

const AUTHORIZE_NET_PAYMENT_ERROR_MESSAGES: Record<AuthorizeNetCode, MessageDescriptor> = {
	"2": msg`Your card was declined. Please try a different card or contact your bank.`,
	"3": msg`Your card was declined. Please try a different card or contact your bank.`,
	"6": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"7": msg`The expiration date is invalid. Please enter a valid expiration date.`,
	"8": msg`Your card has expired. Please use a card with a valid expiration date.`,
	"11": msg`Your card was declined. Please try a different card or contact your bank.`,
	"17": msg`This card type is not accepted here. Please try a different card.`,
	"27": msg`Your postal code does not match your billing address. Please verify and try again.`,
	"37": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"44": msg`Your card's security code (CVV/CVC) was declined. Please check the 3-digit code on the back of your card.`,
	"45": msg`Your card's security code (CVV/CVC) was declined. Please check the 3-digit code on the back of your card.`,
	"65": msg`Your card's security code (CVV/CVC) was declined. Please check the 3-digit code on the back of your card.`,
	"78": msg`Your card's security code (CVV/CVC) is invalid. Please check the 3-digit code on the back of your card.`,
	"315": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"316": msg`The expiration date is invalid. Please enter a valid expiration date.`,
	"317": msg`Your card has expired. Please use a card with a valid expiration date.`,
};

// TouchNet's card-decline codes, from its error code reference.
const TOUCHNET_PAYMENT_ERROR_MESSAGES: Record<string, MessageDescriptor> = {
	"-1702": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"-1703": msg`Your card was declined. Please try a different card or contact your bank.`,
	"-1724": msg`Your postal code does not match your billing address. Please verify and try again.`,
	"-1725": msg`Your card has expired. Please use a card with a valid expiration date.`,
	"-1741": msg`Your card's security code (CVV/CVC) was declined. Please check the 3-digit code on the back of your card.`,
	"-1760": msg`Your card number is invalid. Please check and re-enter your card number.`,
	"-1761": msg`The expiration date is invalid. Please enter a valid expiration date.`,
	"-1765": msg`Your card was declined. Please try a different card or contact your bank.`,
	"-1777": msg`Your card was declined. Please try a different card or contact your bank.`,
	"-1778": msg`Your card was declined. Please try a different card or contact your bank.`,
};

type PaymentErrorAlertProps = {
	error?: PaymentError;
};

const isStripeCode = (code: string | undefined): code is StripeCode => !!code && code in STRIPE_PAYMENT_ERROR_MESSAGES;
const isAuthorizeNetCode = (code: string | undefined): code is AuthorizeNetCode =>
	!!code && code in AUTHORIZE_NET_PAYMENT_ERROR_MESSAGES;

const getStripeCode = (error: StripeLikeError): StripeCode | undefined => {
	if (isStripeCode(error.decline_code)) {
		return error.decline_code;
	}
	if (isStripeCode(error.code)) {
		return error.code;
	}
	return undefined;
};

const getStripeMessage = (error: StripeLikeError): MessageDescriptor => {
	const code = error.message ? getStripeCode(error) : undefined;
	if (code) {
		return STRIPE_PAYMENT_ERROR_MESSAGES[code];
	}
	return error.type === "card_error" || error.type === "validation_error"
		? NOT_PAID_MESSAGE
		: DEFAULT_PAYMENT_ERROR_MESSAGE;
};

export const PaymentErrorAlert = ({ error }: PaymentErrorAlertProps) => {
	const { i18n, t } = useLingui();
	if (error?.type === "unconfirmed") {
		return (
			<Alert
				type="error"
				title={t({
					comment:
						"Title of the payment step error shown when the card payment may or may not have gone through",
					message: "We couldn't confirm your payment",
				})}
				message={t({
					comment:
						"Body of the payment step error shown when the card payment may or may not have gone through; the parker can press Pay again to retry",
					message: "Check your email for a receipt before trying again. If you didn't get one, try again.",
				})}
			/>
		);
	}
	const getMessage = () => {
		if (!error) {
			return i18n._(DEFAULT_PAYMENT_ERROR_MESSAGE);
		}
		if (error.stripeError) {
			return i18n._(getStripeMessage(error.stripeError));
		}
		if (error.type === "declined" && error.authorizeNet) {
			const { code, message } = error.authorizeNet;
			if (isAuthorizeNetCode(code)) {
				return i18n._(AUTHORIZE_NET_PAYMENT_ERROR_MESSAGES[code]);
			}
			if (code && message) {
				return message;
			}
		}
		if (error.type === "declined" && error.touchNet) {
			// TouchNet asks partners to show the code so the parker can quote it to support.
			const { code } = error.touchNet;
			const reason = i18n._(TOUCHNET_PAYMENT_ERROR_MESSAGES[code] ?? NOT_PAID_MESSAGE);
			return `${reason} ${t({
				comment:
					"Follows a card decline message; the code comes from the payment gateway, for the parker to quote to support",
				message: `Error code: ${code}`,
			})}`;
		}
		return i18n._(NOT_PAID_MESSAGE);
	};
	return <Alert type={"error"} title={t`Payment Failed`} message={getMessage()} />;
};
